import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, MapPin, ArrowRight } from "lucide-react";
import { getItineraryBySlug, queueLoadItinerary, type SavedItinerary } from "@/lib/saved-itineraries";
import { NavigationReadinessBar } from "@/components/NavigationReadinessBar";

export const Route = createFileRoute("/r/$slug")({
  loader: async ({ params }) => {
    try {
      const item = await getItineraryBySlug(params.slug);
      return { item };
    } catch {
      return { item: null as SavedItinerary | null };
    }
  },
  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    const SITE = "https://www.routify-tour.com";
    const ogImage = `${SITE}/api/public/og/route/${params.slug}`;
    const canonical = `${SITE}/r/${params.slug}`;

    if (!item) {
      return {
        meta: [
          { title: "Shared route — Routify" },
          { property: "og:title", content: "Shared route — Routify" },
          { property: "og:url", content: canonical },
        ],
        links: [{ rel: "canonical", href: canonical }],
      };
    }

    const stopCount = item.stops?.length ?? 0;
    const title = `${item.title} — ${item.city}`;
    const description = `A tourist route in ${item.city} with ${stopCount} stop${stopCount === 1 ? "" : "s"}. Open in Routify to navigate.`;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: SharedRoutePage,
});

function SharedRoutePage() {
  const { item: initialItem } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<SavedItinerary | null>(initialItem);
  const [loading, setLoading] = useState(!initialItem);
  const [error, setError] = useState<"unavailable" | "failed" | null>(
    initialItem ? null : "unavailable",
  );

  useEffect(() => {
    if (initialItem) return;
    let cancelled = false;
    setLoading(true);
    getItineraryBySlug(slug)
      .then((row) => {
        if (cancelled) return;
        if (!row) setError("unavailable");
        else { setItem(row); setError(null); }
      })
      .catch(() => { if (!cancelled) setError("failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, initialItem]);

  function openInApp() {
    if (!item) return;
    queueLoadItinerary(item);
    navigate({ to: "/" });
  }

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-2xl px-4 py-8">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> A carregar rota partilhada…
        </div>
      )}

      {/* Estado claro e público: nunca redireciona para o login. */}
      {!loading && error && !item && (
        <div className="rounded-2xl border border-dashed border-border bg-background/60 p-8 text-center">
          <h1 className="text-lg font-semibold">
            {error === "failed" ? "Não foi possível carregar a rota" : "Esta ligação já não está ativa"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error === "failed"
              ? "Ocorreu um problema temporário a ler a rota partilhada. Tenta novamente dentro de momentos."
              : "A ligação pode ter sido revogada pelo autor, ter expirado ou a rota ter passado a privada. Não precisas de conta para ver rotas partilhadas — só para as editar ou guardar."}
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: "/" })}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Criar a minha rota <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}


      {!loading && item && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Rota partilhada</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{item.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{item.city}</span>
            <span>•</span>
            <span>{item.stops?.length ?? 0} paragens</span>
            {item.start_time && (<><span>•</span><span>Início {item.start_time}</span></>)}
          </div>

          {item.stops?.length > 0 && (() => {
            const first = item.stops.find((s) => typeof s.lat === "number" && typeof s.lng === "number");
            return (
              <NavigationReadinessBar
                className="mt-4"
                startPoint={first ? { lat: first.lat as number, lng: first.lng as number } : null}
                startPointName={item.stops[0]?.name}
              />
            );
          })()}

          {item.stops?.length > 0 && (
            <ol className="mt-4 space-y-2">
              {item.stops.map((s, i) => (
                <li key={i} className="rounded-xl border border-border/60 bg-background/60 p-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.category}</div>
                      {s.blurb && <p className="mt-1 text-xs text-muted-foreground">{s.blurb}</p>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <button
            type="button"
            onClick={openInApp}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Abrir esta rota no mapa <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </main>
  );
}
